import React from "react";

import { getDispensary } from "@/api/requests/getDispensary";
import getDispensaryReviews from "@/api/requests/getDispensaryReviews";
import { DISPENSARY_PAGES } from "@/constants/dispensary";
import { Dispensary } from "@/custom-types/Dispensary";
import { DispensaryReview } from "@/custom-types/Reviews";
import { retailerClinicMetaData } from "@/lib/retailerMetadata";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import isDispensaryUnclaimed from "@/utils/dispensary/isDispensaryUnclaimed";
import generateDispensaryRedirect from "@/utils/generateDispensaryRedirect";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { getCountryCodeHeader } from "@/utils/requestContext";

import AboutDispensary from "@/components/Dispensary/AboutDispensary";
import DispensaryHeader from "@/components/Dispensary/DispensaryHeader";
import DispensaryTabs from "@/components/Dispensary/DispensaryTabs";
import DoctorClaimPanel from "@/components/Dispensary/DoctorClaimPanel";
import MetaTags from "@/components/MetaTags";

type DoctorPageProps = {
  dispensary: Dispensary;
  reviews: DispensaryReview[];
};

const DoctorPage: React.FC<DoctorPageProps> = ({ dispensary, reviews }) => {
  const { coverPhotoUrl, logoUrl, name } = dispensary;
  const { description, image, title } = retailerClinicMetaData(
    coverPhotoUrl,
    logoUrl,
    name,
  );
  const isUnclaimed = isDispensaryUnclaimed(dispensary);

  return (
    <>
      <MetaTags description={description} image={image} title={title} />
      <DispensaryHeader dispensary={dispensary} isMainPage={true} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.main}
        dispensary={dispensary}
      />
      <div className="mt-xl">
        {isUnclaimed ? (
          <DoctorClaimPanel />
        ) : (
          <AboutDispensary dispensary={dispensary} reviews={reviews} />
        )}
      </div>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<DoctorPageProps>(
  async (context) => {
    const { query, req } = context;
    const { slug } = query;

    const dispensary = await throwOnRedirectHint(
      () => getDispensary(slug as string),
      slug as string,
    );

    const domainCountryCode = getCountryCodeHeader(req.headers);

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      context.resolvedUrl,
      context.query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    // fallback if redirect not generated
    if (!dispensary) {
      return {
        notFound: true,
      };
    }

    const { reviews } = await getDispensaryReviews(dispensary.slug, 0, 4);

    return {
      props: { dispensary, reviews },
    };
  },
);

export default DoctorPage;
